import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  Stack,
  IconButton,
} from '@mui/material';
import CustomPagination from '../../../components/common/CustomPagination';
import TableSkeleton from '../../../components/common/TableSkeleton';
import style from '../Advance/Advance.module.css';
import useCurrency from '../../../components/CurrencyProvider';
import { InvoiceFilterValues } from '../../../components/Billing/Invoice/InvoiceFilter';
import { getRefundListsURL, RefundListResponse, RefundListItem } from './Refund.helper';
import { useAppQuery } from '../../../services/appQuery';
import { getRequest } from '../../../services/httpService';
import { formatDateDMY } from '../../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PrintIcon } from '../../../assets/SVG/printerblue.svg';
import ReusableReportComponent from '../../../components/printReports/ReusableReportComponent';
import RefundReceiptComponent from './RefundReceiptComponent';

export interface RefundedListProps {
  search: string;
  filters: InvoiceFilterValues;
  patientId?: number;
}



const RefundedList: React.FC<RefundedListProps> = ({ search, filters, patientId }) => {
  const { formatCurrency } = useCurrency();
  const [page, setPage] = useState(0);
  const { t: tBilling } = useTranslation('billing_config');
  const [pageSize, setPageSize] = useState(10);
  const [showPrint, setShowPrint] = useState<boolean>(false);
  const [receiptData, setReceiptData] = useState<RefundListItem | null>(null);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);

  const handleClose = () => { setShowPrint(false); };

  const handlePrintReceipt = (row: RefundListItem) => {
    setReceiptData(row);
    setShowPrint(true);
  };

  
  const { data: requestedRefunds, isLoading} = useAppQuery<RefundListResponse>(
    [
      'RefundedList',
      page,
      pageSize,
      search,
      filters.fromDate,
      filters.toDate,
      patientId
    ],
    () => {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String(page * pageSize));
      params.set('status', 'REFUNDED');
      if (patientId) params.set('patient_id', String(patientId));
      if (search) params.set('search', search);
      if (filters.fromDate)
        params.set('from_date', filters.fromDate);
      if (filters.toDate)
        params.set('to_date', filters.toDate);
      return getRequest<RefundListResponse>(`${getRefundListsURL()}?${params.toString()}`);
    },
    { 
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true
    }
  );

  const columns = useMemo(
    () => {
      const baseColumns = [
        tBilling('Refund.date', 'Date'),
        tBilling('Refund.refund_against', 'Refund Against'),
        tBilling('Refund.receipt_no', 'Bill/Advance Number'),
        tBilling('Refund.refund_receipt_no', 'Receipt No.'),
        tBilling('Refund.total_amt', 'Total Amt.'),
        tBilling('Refund.concession_amt', 'Concession Amt.'),
        tBilling('Refund.net_amt', 'Net Amt.'),
        tBilling('Refund.requested_amt', 'Requested Amt.'),
        tBilling('Refund.available_balance', 'Available Balance'),
        tBilling('Refund.refund_reason', 'Refund Reason'),
        tBilling('Refund.action', 'Action'),
      ] as const;

      if (patientId) {
        return baseColumns;
      }

      return [
        tBilling('Refund.date', 'Date'),
        tBilling('Refund.refund_against', 'Refund Against'),
        tBilling('Refund.patient', 'Patient'),
        tBilling('Refund.receipt_no', 'Bill/Advance Number'),
        tBilling('Refund.refund_receipt_no', 'Receipt No.'),
        tBilling('Refund.total_amt', 'Total Amt.'),
        tBilling('Refund.concession_amt', 'Concession Amt.'),
        tBilling('Refund.net_amt', 'Net Amt.'),
        tBilling('Refund.requested_amt', 'Requested Amt.'),
        tBilling('Refund.available_balance', 'Available Balance'),
        tBilling('Refund.refund_reason', 'Refund Reason'),
        tBilling('Refund.action', 'Action'),
      ];
    },
    [patientId, tBilling]
  );

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };


  return (
    <><TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', elevation: 0 }} data-testid="refunded-list-table-container">
      <Table aria-label={tBilling('Refund.aria.refundedTable', 'Refunded list')} data-testid="refunded-list-table">
        <TableHead>
          <TableRow sx={{ bgcolor: '#FAFAFA' }}>
            {columns.map((col) => (
              <TableCell key={col} className={style.tableHeader} sx={{ whiteSpace: 'nowrap' }}>
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && (
            <TableSkeleton
              columnWidths={patientId
                ? [120, 160, 200, 200, 140, 140, 140, 140, 140, 240, 100]
                : [120, 160, 260, 200, 200, 140, 140, 140, 140, 140, 240, 100]
              }
              rows={pageSize} />
          )}

          {!isLoading && requestedRefunds?.objects &&
                      requestedRefunds.objects.map((row) => (
                        <TableRow hover key={row.refund_id} >
                          <TableCell className={style.tableCells}>{row.refund_date ? formatDateDMY(row.refund_date) : '-'}</TableCell>
                          <TableCell className={style.tableCells}>{row.refund_against ?? '-'}</TableCell>
                          {!patientId && (
                            <TableCell className={style.tableCells}>{row.patient_name ?? '-'}</TableCell>
                          )}
                          <TableCell className={style.tableCells}>{row.bill_advance_number ?? '-'}</TableCell>
                          <TableCell className={style.tableCells}>{row.receipt_no ?? '-'}</TableCell>
                          <TableCell className={style.tableCells}>
                            {row.total_amount ? formatCurrency(Number(row.total_amount)) : '-'}
                          </TableCell>
                          <TableCell className={style.tableCells}>
                            {row.concession_amount ? formatCurrency(Number(row.concession_amount)) : '-'}
                          </TableCell>
                          <TableCell className={style.tableCells}>
                            {row.net_amount ? formatCurrency(Number(row.net_amount)) : '-'}
                          </TableCell>
                          <TableCell className={style.tableCells}>
                            {row.requested_amount ? formatCurrency(Number(row.requested_amount)) : '-'}
                          </TableCell>
                          <TableCell className={style.tableCells}>
                            {row.available_balance ? formatCurrency(Number(row.available_balance)) : '-'}
                          </TableCell>
                          <TableCell className={style.tableCells}>{row.refund_reason ?? '-'}</TableCell>
                          <TableCell align="right" className={style.tableCells} sx={{ width: 100 }}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton 
                                size="small" 
                                aria-label={tBilling('Refund.aria.print_receipt', { defaultValue: 'Print receipt' })} 
                                onClick={() => handlePrintReceipt(row)} 
                                disabled={!row.can_print}
                              >
                                <PrintIcon />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}

          {!isLoading && requestedRefunds?.objects?.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">{tBilling('Refund.no_entries_found', 'No entries found.')}</Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
    <CustomPagination
      page={page}
      pageSize={pageSize}
      totalCount={requestedRefunds?.meta?.total_count || 0}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      pageSizeOptions={[10, 15, 25, 50]}
    />

    <ReusableReportComponent
      open={showPrint}
      onClose={handleClose}
      reportTitle={tBilling('Refund.refund_receipt', { defaultValue: 'Refund Receipt' })}
    >
      {receiptData ? (
        <RefundReceiptComponent data={receiptData} />
      ) : (
        <Typography variant="h6" sx={{ mb: 2 }}>{tBilling('Refund.messages.no_receipt_data', { defaultValue: 'No receipt data available.' })}</Typography>
      )}
    </ReusableReportComponent>
    </>
  );
};

export default RefundedList;
